/**
 * useSourceRoster.ts
 * Data + mutations for the per-week WORK-ORDER / QUOTE staffing roster
 * (PR B — D3, the PRIMARY allocation surface).
 *
 * Owns the RosterView for the plan's source under the active scenario and the
 * three mutations (add / set % / remove). Edits are OPTIMISTIC and the % writes
 * are DEBOUNCED per (user, week) so dragging the stepper settles to one PUT.
 * Over-100% never blocks (D4) — the write always goes through and the returned
 * `weekOverloaded` flag drives an inline "over capacity" note. A day-off
 * collision (409 ABSENCE_DAY_CONFLICT) is surfaced verbatim and the optimistic
 * change reverts (Q3B — no bypass).
 *
 * On any successful mutation the parent is notified (onAllocationChanged) so the
 * capacity grid + the live schedules re-fetch (they read the same allocation %).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getRoster,
  setRosterAllocation,
  removeRosterAllocation,
  type RosterSource,
  type RosterView,
  type RosterWeek,
} from '../../../../services/scheduleRoster';

interface UseSourceRosterArgs {
  source: RosterSource | null;
  sourceKey: string | null;
  scenarioKey: string | null;
  /** When false (e.g. plan locked) the hook loads read-only; mutations are refused. */
  enabled?: boolean;
  /** Called after any successful add / set% / remove so the parent can refresh. */
  onAllocationChanged?: () => void;
}

interface UseSourceRosterResult {
  weeks: RosterWeek[];
  loading: boolean;
  loadError: string | null;
  /** Transient mutation error (day-off block, lock, network) — shown inline. */
  mutationError: string | null;
  clearMutationError: () => void;
  reload: () => void;
  /** Set (or add) a person's % for a week. Optimistic + debounced. */
  setAllocation: (userKey: string, displayName: string, weekStart: string, percent: number) => void;
  /** Remove a person from a week's roster. Optimistic. */
  removeMember: (userKey: string, weekStart: string) => void;
}

/** Deep-clone the weeks array so optimistic edits never mutate React state in place. */
function cloneWeeks(weeks: RosterWeek[]): RosterWeek[] {
  return weeks.map((w) => ({ ...w, entries: w.entries.map((e) => ({ ...e })) }));
}

export function useSourceRoster({
  source,
  sourceKey,
  scenarioKey,
  enabled = true,
  onAllocationChanged,
}: UseSourceRosterArgs): UseSourceRosterResult {
  const [weeks, setWeeks] = useState<RosterWeek[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const fetchIdRef = useRef(0);
  // Debounce timers keyed by `${userKey}::${weekStart}` so each cell settles once.
  const debounceRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const reload = useCallback(() => {
    if (!source || !sourceKey) {
      setWeeks([]);
      return;
    }
    const id = ++fetchIdRef.current;
    setLoading(true);
    setLoadError(null);
    getRoster(source, sourceKey, scenarioKey ?? undefined)
      .then((view: RosterView) => {
        if (fetchIdRef.current !== id) return;
        setWeeks(view.weeks);
      })
      .catch((err: unknown) => {
        if (fetchIdRef.current !== id) return;
        setLoadError(err instanceof Error ? err.message : 'Failed to load the staffing roster.');
      })
      .finally(() => {
        if (fetchIdRef.current === id) setLoading(false);
      });
  }, [source, sourceKey, scenarioKey]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Snapshot for optimistic-revert: the last server-confirmed weeks.
  const confirmedRef = useRef<RosterWeek[]>([]);
  useEffect(() => {
    confirmedRef.current = cloneWeeks(weeks);
    // Only re-snapshot on load, not on every optimistic edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const clearMutationError = useCallback(() => setMutationError(null), []);

  const setAllocation = useCallback(
    (userKey: string, displayName: string, weekStart: string, percent: number) => {
      if (!enabled || !source || !sourceKey) return;
      const clamped = Math.max(0, Math.min(500, Math.round(percent)));

      // Optimistic local update — add or set the entry.
      setWeeks((prev) => {
        const next = cloneWeeks(prev);
        const wk = next.find((w) => w.weekStart === weekStart);
        if (!wk) return prev;
        const entry = wk.entries.find((e) => e.userKey.toLowerCase() === userKey.toLowerCase());
        if (entry) {
          entry.percent = clamped;
          entry.hasAllocationRow = true;
          entry.present = true;
        } else {
          wk.entries.push({ userKey, displayName, percent: clamped, present: true, hasAllocationRow: true });
          wk.entries.sort((a, b) => a.displayName.localeCompare(b.displayName));
        }
        return next;
      });

      const key = `${userKey}::${weekStart}`;
      const timers = debounceRef.current;
      const existing = timers.get(key);
      if (existing) clearTimeout(existing);
      timers.set(
        key,
        setTimeout(() => {
          timers.delete(key);
          setRosterAllocation({
            source,
            sourceKey,
            scenarioKey: scenarioKey ?? undefined,
            userKey,
            weekStart,
            percent: clamped,
          })
            .then(() => {
              setMutationError(null);
              onAllocationChanged?.();
            })
            .catch((err: unknown) => {
              // Revert to the last confirmed state and surface the message.
              setWeeks(cloneWeeks(confirmedRef.current));
              setMutationError(err instanceof Error ? err.message : 'Could not save the allocation.');
            });
        }, 350),
      );
    },
    [enabled, source, sourceKey, scenarioKey, onAllocationChanged],
  );

  const removeMember = useCallback(
    (userKey: string, weekStart: string) => {
      if (!enabled || !source || !sourceKey) return;

      setWeeks((prev) => {
        const next = cloneWeeks(prev);
        const wk = next.find((w) => w.weekStart === weekStart);
        if (wk) wk.entries = wk.entries.filter((e) => e.userKey.toLowerCase() !== userKey.toLowerCase());
        return next;
      });

      removeRosterAllocation({
        source,
        sourceKey,
        scenarioKey: scenarioKey ?? undefined,
        userKey,
        weekStart,
      })
        .then(() => {
          setMutationError(null);
          onAllocationChanged?.();
        })
        .catch((err: unknown) => {
          setWeeks(cloneWeeks(confirmedRef.current));
          setMutationError(err instanceof Error ? err.message : 'Could not remove the person.');
        });
    },
    [enabled, source, sourceKey, scenarioKey, onAllocationChanged],
  );

  // Flush any pending debounce timers on unmount.
  useEffect(() => {
    const timers = debounceRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  return {
    weeks,
    loading,
    loadError,
    mutationError,
    clearMutationError,
    reload,
    setAllocation,
    removeMember,
  };
}
